import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Switch,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  Link,
  MusicNote,
  Vibration,
  VolumeUp,
  Apps,
  AccountCircle,
  ContentCopy,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { styled } from '@mui/material/styles';

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 36,
  height: 22,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(14px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#ffc107',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 18,
    height: 18,
  },
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
  },
}));

const OtherDetails = ({ rowValue }) => {
  const [otherDetails, setOtherDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOtherDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_URL}/api/admin/player/get-other-details/${rowValue._id}`
        );

        if (response.data.status) {
          setOtherDetails(response.data.data);
        } else {
          setError(response.data.message || "Failed to fetch other details");
        }
      } catch (err) {
        setError(
          err.message || "An error occurred while fetching other details"
        );
      } finally {
        setLoading(false);
      }
    };

    if (rowValue?._id) {
      fetchOtherDetails();
    }
  }, [rowValue]);

  const handleCopy = (text) => {
    if (!text || text === "Not available") return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const getAppInstallType = (type) => {
    switch (type) {
      case 0:
        return "Play Store";
      case 1:
        return "Web";
      case 2:
        return "App Store";
      case 3:
        return "Teenpatti";
      default:
        return "Unknown";
    }
  };

  const getLoginType = (type) => {
    switch (type) {
      case 0:
        return "Guest";
      case 1:
        return "Phone Number";
      case 2:
        return "Email";
      case 3:
        return "Google";
      case 4:
        return "Facebook";
      case 5:
        return "Apple";
      default:
        return "Unknown";
    }
  };

  const getLoginTypeValue = (type) => {
    switch (type) {
      case 0:
        return "Free";
      case 1:
        return "Cash";
      default:
        return "Unknown";
    }
  };

  return (
    <Box>
      {otherDetails ? (
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3} direction="column">
            {/* Login Information */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.2rem', mb: 0.5 }}>
                  Login Information
                </Typography>
                <Box sx={{ height: '2px', width: '30px', bgcolor: '#ffc107', borderRadius: '10px' }} />
              </Box>

              <Grid container columnSpacing={4} rowSpacing={1.5}>
                <Grid item xs={6} md={3}>
                  <ListItem sx={{ px: 0, py: 0.1 }}>
                    <ListItemText
                      primary={<Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem' }}>App Install Type</Typography>}
                      secondary={<Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, mt: 0.2, fontSize: '1.1rem' }}>{getAppInstallType(otherDetails.appInstallType)}</Typography>}
                    />
                  </ListItem>
                </Grid>
                <Grid item xs={6} md={3}>
                  <ListItem sx={{ px: 0, py: 0.1 }}>
                    <ListItemText
                      primary={<Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem' }}>Login Type</Typography>}
                      secondary={<Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, mt: 0.2, fontSize: '1.1rem' }}>{getLoginType(otherDetails.loginType)}</Typography>}
                    />
                  </ListItem>
                </Grid>
                <Grid item xs={6} md={3}>
                  <ListItem sx={{ px: 0, py: 0.1 }}>
                    <ListItemText
                      primary={<Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem' }}>Login Type Value</Typography>}
                      secondary={<Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, mt: 0.2, fontSize: '1.1rem' }}>{getLoginTypeValue(otherDetails.loginTypeValue)}</Typography>}
                    />
                  </ListItem>
                </Grid>
                <Grid item xs={6} md={3}>
                  <ListItem sx={{ px: 0, py: 0.1 }}>
                    <ListItemText
                      primary={<Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem' }}>Email Verification</Typography>}
                      secondary={
                        <Chip
                          label={otherDetails.emailVerified ? "Verified" : "Not Verified"}
                          sx={{ 
                            height: 28, 
                            mt: 0.5,
                            fontSize: '0.8rem', 
                            fontWeight: 700,
                            bgcolor: otherDetails.emailVerified ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: otherDetails.emailVerified ? '#16a34a' : '#dc2626',
                            borderRadius: '8px'
                          }}
                        />
                      }
                    />
                  </ListItem>
                </Grid>
                <Grid item xs={6} md={3}>
                  <ListItem sx={{ px: 0, py: 0.1 }}>
                    <ListItemText
                      primary={<Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem' }}>Phone Verification</Typography>}
                      secondary={
                        <Chip
                          label={otherDetails.phoneNoVerified ? "Verified" : "Not Verified"}
                          sx={{ 
                            height: 28, 
                            mt: 0.5,
                            fontSize: '0.8rem', 
                            fontWeight: 700,
                            bgcolor: otherDetails.phoneNoVerified ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: otherDetails.phoneNoVerified ? '#16a34a' : '#dc2626',
                            borderRadius: '8px'
                          }}
                        />
                      }
                    />
                  </ListItem>
                </Grid>
              </Grid>
            </Grid>

            {/* Social & Preferences */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2, mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.2rem', mb: 0.5 }}>
                  Social & Preferences
                </Typography>
                <Box sx={{ height: '2px', width: '30px', bgcolor: '#ffc107', borderRadius: '10px' }} />
              </Box>

              <Grid container columnSpacing={4} rowSpacing={1.5}>
                <Grid item xs={6} md={3}>
                  <ListItem sx={{ px: 0, py: 0.1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem' }}>
                          Referral Code
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2 }}>
                          <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, mr: 1.5, fontSize: '1.1rem' }}>
                            {otherDetails.refercode || "Not available"}
                          </Typography>
                          {otherDetails.refercode && otherDetails.refercode !== "Not available" && (
                            <ContentCopy 
                              sx={{ fontSize: 18, color: '#64748b', cursor: 'pointer', '&:hover': { color: '#ffc107' } }} 
                              onClick={() => handleCopy(otherDetails.refercode)}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Grid>

                <Grid item xs={6} md={3}>
                  <ListItem sx={{ px: 0, py: 0.1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem' }}>
                          Referred By
                        </Typography>
                      }
                      secondary={<Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, mt: 0.2, fontSize: '1.1rem' }}>{otherDetails.referredBy || "Not referred"}</Typography>}
                    />
                  </ListItem>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }} />
                </Grid>

                <Grid item xs={4} md={2}>
                  <Box sx={{ py: 0.1 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                      Sound
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IOSSwitch checked={otherDetails.sound} disabled />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={4} md={2}>
                  <Box sx={{ py: 0.1 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                      Music
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IOSSwitch checked={otherDetails.music} disabled />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={4} md={2}>
                  <Box sx={{ py: 0.1 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                      Vibration
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IOSSwitch checked={otherDetails.vibration} disabled />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{ p: 4, textAlign: "center", bgcolor: 'rgba(248, 250, 252, 0.5)', borderRadius: '16px', border: '1px dashed #e2e8f0' }}
        >
          <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
            No other details available for this player.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OtherDetails;
